import { useEffect } from "react";
import { BookOpen, PenLine, Feather, Trees, CheckCircle2, ArrowUpRight, Play, Store } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import EventsSection from "@/components/EventsSection";
import InquiryForm from "@/components/InquiryForm";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { VIDEOS } from "@/data/content";

const VISIT_FEATURES = [
  "Dynamic read-alouds (English or Spanish)",
  "Q&A with the author",
  "Tree planting or eco-art activity",
  "Signed book sales",
  "In-person or virtual format",
];

export default function Collaborate() {
  useEffect(() => {
    document.title = "Collaborate & Book — Jen Cullerton Johnson";
  }, []);
  return (
    <>
      <PageHero
        eyebrow="Collaborate with Jen"
        title={["Inspire learners. Empower", <span key="c">community. <em className="text-terra">Spark change.</em></span>]}
        lede="Bring books to life — with the author in the room. Jen's visits, workshops, coaching, and forest walks are energetic, interactive, and tailored to your community."
        meta="Schools · Libraries · Book fairs · Community centers · Online & in person"
      />

      <section className="py-20 lg:py-28" data-testid="collaborate-visits">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-lg overflow-hidden border border-line shadow-xl shadow-ink/10">
              <img src="/images/jen-visits.png" alt="Collage of author visit moments — reading, tree planting, and school visits" className="w-full h-80 sm:h-[440px] object-cover photo-tone" data-testid="collaborate-visits-photo" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <Eyebrow className="mb-6">Author visits — Grades Pre-K–8</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.1] mb-6">
              Bring books to life with the author in the room
            </h2>
            <p className="text-txtmut leading-relaxed mb-8">
              Inspire young minds to explore the world, ask big questions, and care for the planet. Perfect for
              schools, libraries, book fairs, and community centers.
            </p>
            <ul className="space-y-3 mb-10">
              {VISIT_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-3 text-ink text-sm sm:text-base" data-testid="visit-feature">
                  <CheckCircle2 className="w-5 h-5 text-terra shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <a href="#booking" className="group inline-flex items-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-8 py-4 font-medium transition-colors duration-300" data-testid="collaborate-book-visit-cta">
              Book an author visit <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream border-y border-line py-20 lg:py-28" data-testid="collaborate-offerings">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Ways to work together</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-14">Offerings for every community</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: BookOpen, title: "Professional Development", desc: "Green Literacy workshops for educators and community leaders — online and in person — integrating reading, writing, and environmental action in K–5 classrooms.", cta: "Request a workshop" },
              { icon: PenLine, title: "Creative Coaching", desc: "One-on-one sessions for writers, educators, artists, and activists. Project brainstorming, picture book & memoir feedback, routines for creativity, accountability check-ins. 30 or 60 minutes, Zoom or phone, one-time or ongoing.", cta: "Book a consult" },
              { icon: Trees, title: "Forest Bathing Sessions", desc: "Nature-guided events in Chicago and the Indiana Dunes with a certified Forest Guide — gentle meditations, mindful exploration, and community connection.", cta: "Reserve a walk" },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="h-full border border-line rounded-lg bg-paper p-8 lg:p-10" data-testid={`collaborate-offering-${i + 1}`}>
                  <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center mb-6">
                    <s.icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-2xl text-ink mb-3">{s.title}</h3>
                  <p className="text-txtmut text-sm leading-relaxed mb-6">{s.desc}</p>
                  <a href="#booking" className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra link-underline">{s.cta}</a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <a href="https://www.jencullertonjohnson.com/store/p2/Seed_idea_to_Story_30_Minute_Consultation.html" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-3 border border-terra/40 hover:bg-goldsoft rounded-lg px-6 py-4 transition-colors duration-300" data-testid="collaborate-consult-link">
              <Store className="w-5 h-5 text-terra shrink-0" />
              <span className="text-sm text-ink">
                <strong>Seed Idea to Story</strong> — 30-minute manuscript consultation, available in the shop
              </span>
              <ArrowUpRight className="w-4 h-4 text-terra" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="collaborate-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow className="mb-6">Forest bathing — Chicago & Indiana Dunes</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.1] mb-6">
              "The forest offers a kind of medicine — steady, ancient, and patient."
            </h2>
            <p className="text-txtmut leading-relaxed mb-6">
              In October 2025, Jen graduated from the Forest Therapy School as a certified Forest Guide. She now
              guides walks in parks and preserves throughout the Chicago area, and monthly at the Indiana Dunes —
              including Gratitude Gatherings with Chicago-based practitioner Allison of Trees & Tranquility.
            </p>
            <a href="#booking" className="group inline-flex items-center gap-2 border border-ink/25 hover:border-terra hover:text-terra text-ink rounded-full px-8 py-4 font-medium transition-colors duration-300" data-testid="collaborate-forest-cta">
              <Feather className="w-4 h-4" /> Book a forest session
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-lg overflow-hidden border border-line shadow-xl shadow-ink/10">
              <img src="/images/forest-walk.jpg" alt="A quiet forest path — Jen's forest bathing practice" className="w-full h-96 object-cover photo-tone" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream border-y border-line py-16 lg:py-20" data-testid="collaborate-video">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-2">See coaching in action</h2>
            <p className="text-txtmut text-sm">First Draft Coaching — a look inside Jen's creative coaching sessions.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <a href={VIDEOS.coaching} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 bg-ink hover:bg-pine text-paper rounded-full px-7 py-3.5 transition-colors duration-300" data-testid="collaborate-video-link">
              <span className="w-9 h-9 rounded-full bg-paper text-ink flex items-center justify-center group-hover:bg-goldsoft transition-colors duration-300">
                <Play className="w-4 h-4 fill-current" />
              </span>
              Watch on YouTube
            </a>
          </Reveal>
        </div>
      </section>

      <EventsSection />

      <section id="booking" className="py-20 lg:py-28 scroll-mt-24" data-testid="collaborate-booking">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow className="mb-6">Booking</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.1] mb-6">Let's plan your visit</h2>
            <p className="text-txtmut leading-relaxed mb-8">
              Tell Jen about your students, your event, or your idea — she'll get back to you as soon as she can.
              Looking forward to connecting!
            </p>
            <div className="rounded-lg overflow-hidden border border-line">
              <img src="/images/event-discovery-center.jpg" alt="Jen reading Seeds of Change with students" className="w-full h-72 object-cover photo-tone" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="border border-line rounded-lg bg-paper p-6 sm:p-10 shadow-sm">
              <InquiryForm defaultInquiry="author-visit" idPrefix="booking" testid="booking-form" />
            </div>
          </Reveal>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
